import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { FileIcon } from './StandardRegulations';

// ATO 지정서는 훈련운영기준/교육기준과 같은 stdregular 테이블에 구분코드 'ATO' 로 저장하고, 이 페이지에서만 관리한다.
// 갱신일자 = updateat, 유효기간 = reason, 지정서 PDF = pdfname/filepath (HWPX 는 쓰지 않는다)
const STDTYPE = 'ATO';

// PDF 뷰어에 붙이는 옵션: 도구 모음/썸네일/스크롤바를 숨기고 한 쪽이 화면 크기에 꼭 맞게 한다
// (표시 영역이 A4 비율이라 가로폭에도 맞는다. view=FitH 는 일부 뷰어에서 적용되지 않았다)
const VIEWER_PARAMS = '#toolbar=0&navpanes=0&scrollbar=0&view=Fit';

const today = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const emptyForm = () => ({ updateat: today(), reason: '', pdfFile: null });

export default function ATOCertificate() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'staff';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [editing, setEditing] = useState(null); // null=닫힘, 'new'=등록, sregid=수정
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const [viewerUrl, setViewerUrl] = useState(null);
  const [viewerState, setViewerState] = useState('idle'); // idle | loading | ready | error
  const [viewTick, setViewTick] = useState(0); // 저장 후 같은 행의 파일이 바뀌었을 때 다시 불러오기 위한 값

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/standards', { params: { stdtype: STDTYPE } });
      setRows(res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const editingRow = typeof editing === 'number' ? rows.find((r) => r.sregid === editing) : null;

  // 갱신일자가 가장 최근인 지정서(목록 첫 줄)를 왼쪽 화면에 보여준다
  const latest = rows[0] || null;
  const latestId = latest && latest.has_pdf ? latest.sregid : null;

  useEffect(() => {
    if (!latestId) {
      setViewerUrl(null);
      setViewerState(loading ? 'loading' : 'idle');
      return undefined;
    }
    let cancelled = false;
    let objectUrl = null;
    setViewerState('loading');
    api
      .get(`/standards/${latestId}/file`, { params: { kind: 'pdf' }, responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        objectUrl = window.URL.createObjectURL(res.data);
        setViewerUrl(objectUrl);
        setViewerState('ready');
      })
      .catch(() => {
        if (!cancelled) setViewerState('error');
      });
    return () => {
      cancelled = true;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [latestId, viewTick, loading]);

  const openNew = () => {
    setForm(emptyForm());
    setEditing('new');
    setMessage('');
  };

  const openEdit = (r) => {
    setForm({ updateat: r.updateat || today(), reason: r.reason || '', pdfFile: null });
    setEditing(r.sregid);
    setMessage('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.updateat) {
      setMessage('갱신일자를 입력하세요.');
      return;
    }
    if (!form.reason.trim()) {
      setMessage('유효기간을 입력하세요.');
      return;
    }
    if (editing === 'new' && !form.pdfFile) {
      setMessage('지정서 PDF 파일을 선택하세요.');
      return;
    }
    const data = new FormData();
    data.append('updateat', form.updateat);
    data.append('reason', form.reason.trim());
    if (form.pdfFile) data.append('pdf_file', form.pdfFile);
    setSaving(true);
    setMessage('');
    try {
      if (editing === 'new') {
        data.append('stdtype', STDTYPE);
        await api.post('/standards', data);
      } else {
        await api.put(`/standards/${editing}`, data);
      }
      setEditing(null);
      await load();
      setViewTick((n) => n + 1);
      setMessage('저장되었습니다.');
    } catch (err) {
      setMessage(err.response?.data?.detail || '저장하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`${r.updateat} 갱신 지정서(${r.reason})를 삭제하시겠습니까?\nPDF 파일도 함께 삭제됩니다.`)) return;
    try {
      await api.delete(`/standards/${r.sregid}`);
      await load();
      setViewTick((n) => n + 1);
      setMessage('삭제되었습니다.');
    } catch (err) {
      setMessage(err.response?.data?.detail || '삭제하지 못했습니다.');
    }
  };

  // 이력의 지정서를 새 탭에서 연다 (인증이 필요한 파일이라 내려받은 뒤 임시 주소로 연다)
  const openPdf = async (r) => {
    const win = window.open('', '_blank');
    try {
      const res = await api.get(`/standards/${r.sregid}/file`, { params: { kind: 'pdf' }, responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      if (win) win.location.href = url;
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      if (win) win.close();
      setMessage('파일을 열 수 없습니다. 서버에 파일이 없거나 삭제되었을 수 있습니다.');
    }
  };

  const downloadLatest = () => {
    if (!viewerUrl || !latest) return;
    const a = document.createElement('a');
    a.href = viewerUrl;
    a.download = latest.pdfname || '경북항공기술교육원 전문교육기관 지정서.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const viewerMessage = () => {
    if (viewerState === 'loading') return '불러오는 중...';
    if (viewerState === 'error') return '지정서 파일을 열 수 없습니다. 서버에 파일이 없거나 삭제되었을 수 있습니다.';
    if (latest && !latest.has_pdf) return '최신 갱신 건에 PDF 파일이 없습니다.';
    return `등록된 지정서가 없습니다.${isAdmin ? ' 위의 [지정서 등록] 버튼으로 추가하세요.' : ''}`;
  };

  return (
    <div className="page ato-cert-page">
      <div className="page-header">
        <h2>경북항공기술교육원 전문교육기관 지정서</h2>
        <div className="ato-actions">
          {viewerState === 'ready' && viewerUrl && (
            <>
              <a className="btn-ghost ato-link-btn" href={viewerUrl} target="_blank" rel="noreferrer">
                새 창으로 보기
              </a>
              <button type="button" className="btn-ghost ato-link-btn" onClick={downloadLatest}>
                PDF 다운로드
              </button>
            </>
          )}
          {isAdmin && editing === null && (
            <button type="button" className="btn-primary std-add-btn" onClick={openNew}>
              지정서 등록
            </button>
          )}
        </div>
      </div>

      {editing !== null && (
        <form className="card std-form" onSubmit={handleSave}>
          <h3>{editing === 'new' ? '지정서 등록' : '지정서 수정'}</h3>
          <div className="std-form-grid ato-form-grid">
            <div className="form-field">
              <label>갱신일자</label>
              <input type="date" value={form.updateat} onChange={(e) => setForm({ ...form, updateat: e.target.value })} />
            </div>
            <div className="form-field">
              <label>유효기간</label>
              <input
                type="text"
                maxLength={100}
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="예) 2025.02.12 ~ 2030.02.11"
              />
            </div>
          </div>
          <div className="form-field">
            <label>지정서 PDF 파일 (.pdf)</label>
            <input
              type="file"
              accept=".pdf,application/pdf"
              onChange={(e) => setForm({ ...form, pdfFile: e.target.files?.[0] || null })}
            />
            {editingRow?.pdfname && <span className="std-hint">현재: {editingRow.pdfname} (바꿀 때만 선택)</span>}
          </div>
          <span className="std-hint std-hint-block">파일은 30MB 이하입니다. 갱신일자가 가장 최근인 지정서가 왼쪽 화면에 나타납니다.</span>
          <div className="std-form-actions">
            <button type="submit" className="btn-primary std-save-btn" disabled={saving}>
              {saving ? '저장 중...' : '저장'}
            </button>
            <button type="button" className="btn-ghost" onClick={() => setEditing(null)}>취소</button>
          </div>
        </form>
      )}

      {message && <div className="form-message std-message">{message}</div>}

      <div className="ato-cert-layout">
        <div className="card ato-viewer-card">
          <div className="ato-paper-frame">
            {viewerState === 'ready' && viewerUrl ? (
              <iframe title="경북항공기술교육원 전문교육기관 지정서" src={`${viewerUrl}${VIEWER_PARAMS}`} />
            ) : (
              <div className="ato-viewer-empty">{viewerMessage()}</div>
            )}
          </div>
        </div>

        <div className="card ato-hist-card">
          <h3 className="ato-hist-title">갱신이력</h3>
          <div className="table-scroll">
            <table className="data-table ato-hist-table">
              <thead>
                <tr>
                  <th className="ato-col-date">갱신일자</th>
                  <th>유효기간</th>
                  <th className="ato-col-file">지정서</th>
                  {isAdmin && <th className="ato-col-admin">관리</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.sregid}>
                    <td>
                      {r.updateat}
                      {i === 0 && <span className="std-current-badge">현행</span>}
                    </td>
                    <td className="ato-period">{r.reason}</td>
                    <td>
                      <div className="std-file-cell">
                        <span className="std-file-name">{r.pdfname || '파일 없음'}</span>
                        <span className="std-file-icons">
                          <button
                            type="button"
                            className="std-icon-btn"
                            disabled={!r.has_pdf}
                            onClick={() => openPdf(r)}
                            title={r.has_pdf ? 'PDF 파일 열기' : 'PDF 파일 없음'}
                            aria-label="PDF 파일 열기"
                          >
                            <FileIcon kind="pdf" />
                          </button>
                        </span>
                      </div>
                    </td>
                    {isAdmin && (
                      <td className="std-admin">
                        <button type="button" className="btn-ghost std-small-btn" onClick={() => openEdit(r)}>수정</button>
                        <button type="button" className="btn-ghost std-small-btn std-del-btn" onClick={() => handleDelete(r)}>삭제</button>
                      </td>
                    )}
                  </tr>
                ))}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={isAdmin ? 4 : 3} className="empty-row">갱신이력이 없습니다.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
